import React, { FunctionComponent, useRef, useMemo, useCallback } from 'react';
import { KeyPath } from 'app/models/entityStateSync/keypath';
import { surfaceResolution } from 'app/models/rtc/draw/resolution';
import { TraceType } from 'app/models/rtc/draw/trace';
import { useDrawToolTemplate, DrawToolTemplate } from 'app/components/rtc/draw/DrawContext';
import { decodeOrElse, decodeOrThrow } from 'app/utils/ioTs';
import * as iots from 'io-ts';
import { IColor, ColorCodec } from '@engoo/realtime-protocol/util/color';
import { useCallerColor } from 'app/components/call/SharedSelection/colors';
import { BLACK } from 'app/models/rtc/draw/color';
import { useCss } from 'app/utils/css';
import { ISize } from 'app/components/layout/useMeasure';
import { DEFAULT_ERASER_SIZE, DEFAULT_PENCIL_SIZE } from 'app/models/rtc/draw/constants';

interface IDrawSurfaceProps {
  keyPath: KeyPath;
  size: ISize;
  tool: TraceType;
}

type DrawInputEvent = React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>;

/**
 * Handles drawing mouse and touch input
 *
 * Scales original input's coords to the current resolution of the surface
 */
export const DrawInputSurface: FunctionComponent<IDrawSurfaceProps> = ({ keyPath, size, tool }) => {
  const css = useCss();
  const callerColor = useCallerColor();
  const isDrawing = useRef(false);

  // Input coords will be scaled to fit within the calculated
  // input resolution
  const resolution = surfaceResolution(size.width, size.height);
  const horizontalScale = resolution.width / size.width;
  const verticalScale = resolution.height / size.height;

  const color: IColor = useMemo(() => decodeOrElse(ColorCodec, callerColor, () => BLACK), [callerColor]);

  const template: DrawToolTemplate = useMemo(() => {
    switch (tool) {
      case TraceType.Pencil:
        return {
          type: TraceType.Pencil,
          size: DEFAULT_PENCIL_SIZE,
          color,
        };
      case TraceType.Eraser:
        return {
          type: TraceType.Eraser,
          size: DEFAULT_ERASER_SIZE,
        };
    }
  }, [tool, color]);

  const { startShape, addPoint } = useDrawToolTemplate({ keyPath, resolution, template });

  const validateEvent = useCallback((event: DrawInputEvent) => {
    if (
      ('button' in event && event.button !== 0)
      || ('targetTouches' in event && event.targetTouches.length > 1)
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.shiftKey
    ) {
      isDrawing.current = false;
      return false;
    }

    event.preventDefault();
    event.stopPropagation();

    return true;
  }, []);

  // Maps coords of the event to the resolution of drawing surface
  const mapEventCoords = useCallback((event: DrawInputEvent): [iots.Int, iots.Int] => {
    const position = event.currentTarget.getBoundingClientRect();

    // Normalize mouse and touch events. We could've used
    // PointerEvents but it's quite new for Safari
    const { clientX, clientY } = 'touches' in event
      ? (event.touches[0] || { clientX: 0, clientY: 0 })
      : event;

    // Map input onto the current resolution and round it to int
    const x = decodeOrThrow(iots.Int, Math.round((clientX - position.left) * horizontalScale));
    const y = decodeOrThrow(iots.Int, Math.round((clientY - position.top) * verticalScale));

    return [x, y];
  }, [horizontalScale, verticalScale]);

  const onStart = useCallback((event: DrawInputEvent) => {
    if (!validateEvent(event)) {
      return;
    }

    isDrawing.current = true;

    startShape(...mapEventCoords(event));
  }, [startShape, validateEvent, mapEventCoords]);

  const onMove = useCallback((event: DrawInputEvent) => {
    if (!validateEvent(event)) {
      return;
    }

    if (isDrawing.current) {
      addPoint(...mapEventCoords(event));
    }
  }, [addPoint, validateEvent, mapEventCoords]);

  const onEnd = useCallback(() => {
    isDrawing.current = false;
  }, []);

  return <div
    onMouseDown={onStart}
    onMouseMove={onMove}
    onMouseUp={onEnd}
    onTouchStart={onStart}
    onTouchMove={onMove}
    onTouchEnd={onEnd}
    onTouchCancel={onEnd}
    className={css('drawInput', {
      position: 'absolute',
      top: 0,
      left: 0,
      width: `${size.width}px`,
      height: `${size.height}px`,
      cursor: 'crosshair',
      touchAction: 'pinch-zoom',
    })}
  />;
};

export default DrawInputSurface;
